import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { RequestSupportSessionDrawer } from '@client/components/admin-console/support-session/RequestSupportSessionDrawer';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { getRoleDisplayName } from '@client/utils/roles/role-config';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { SUPPORT_SESSION_STATUS_LABELS, type SupportSessionStatus } from '@shared/constants/business/support-session';
import type { Role } from '@shared/constants/business/user';
import { createFileRoute } from '@tanstack/react-router';
import { Modal, Space, Tag, Tooltip } from 'antd';
import { Headphones, Plus, Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { SupportSessionDetailsDrawer } from './SupportSessionDetailsDrawer';
import { SupportSessionFilters } from './SupportSessionFilters';

export const Route = createFileRoute('/admin-console/user-access/support-sessions')({
    staticData: {
        permissions: ['admin_support:read'],
        breadcrumb: {
            title: 'Support sessions',
        },
        ui: {
            icon: Headphones,
            title: 'Support sessions',
            subtitle: 'Manage support session requests across all organisations',
            widthVariant: 'fullWidth',
            menuDescription: 'Review support access requests',
        },
    },
    component: SupportSessionsPage,
});

type SessionRow = RouterOutputs['admin']['supportSession']['list']['rows'][number];
type QueryVariables = RouterInputs['admin']['supportSession']['list'];
type OrderBy = NonNullable<QueryVariables['orderBy']>;

const columnHelper = createColumnHelper<SessionRow, OrderBy>();

function getStatusColor(status: SupportSessionStatus): 'blue' | 'green' | 'red' | 'default' | 'orange' {
    switch (status) {
        case 'pending':
            return 'blue';
        case 'approved':
            return 'green';
        case 'rejected':
            return 'red';
        case 'expired':
            return 'default';
        case 'revoked':
            return 'orange';
        default:
            return 'default';
    }
}

function SupportSessionsPage() {
    const { trpc, trpcClient, trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const { canEdit: canRequestSupportSession } = useAdminModulePermissions('support');

    const [selectedSession, setSelectedSession] = useState<SessionRow | null>(null);
    const [isRequestDrawerOpen, setIsRequestDrawerOpen] = useState(false);
    const [filters, setFilters] = useState<QueryVariables>({});

    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { createdAt: 'desc' },
    });

    const { data, refetch, isFetching, error } = trpc.admin.supportSession.list.useQuery({
        ...queryVariables,
        ...filters,
    });

    const handleCancel = async (session: SessionRow) => {
        Modal.confirm({
            title: 'Cancel support session request',
            content: `Are you sure you want to cancel the support session request for "${session.Org.name}"?`,
            okText: 'Cancel Request',
            okButtonProps: { danger: true },
            cancelText: 'Keep Request',
            onOk: async () => {
                try {
                    await trpcClient.admin.supportSession.cancel.mutate({ id: session.id });
                    message.success('Support session request cancelled');
                    void trpcUtils.admin.supportSession.list.invalidate();
                } catch (err) {
                    errorMessage.show(err);
                }
            },
        });
    };

    const handleRevoke = async (session: SessionRow) => {
        const supportUserName =
            session.SupportUser.firstName || session.SupportUser.lastName
                ? `${session.SupportUser.firstName ?? ''} ${session.SupportUser.lastName ?? ''}`.trim()
                : session.SupportUser.email;

        Modal.confirm({
            title: 'Revoke support session',
            content: `Are you sure you want to revoke access for "${supportUserName}" to "${session.Org.name}"? This will immediately disable the support account's access.`,
            okText: 'Revoke Access',
            okButtonProps: { danger: true },
            cancelText: 'Keep Active',
            onOk: async () => {
                try {
                    await trpcClient.admin.supportSession.revoke.mutate({ id: session.id });
                    message.success('Support session revoked');
                    void trpcUtils.admin.supportSession.list.invalidate();
                } catch (err) {
                    errorMessage.show(err);
                }
            },
        });
    };

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
            width: 80,
            align: 'center',
        }),
        columnHelper.accessor('Org', {
            id: 'org',
            header: 'Organisation',
            cell: ({ value }) => (
                <Button.Link
                    variant="link"
                    to="/admin-console/organisation-management/organisations/$orgId"
                    params={{ orgId: value.id }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {value.name}
                </Button.Link>
            ),
        }),
        columnHelper.accessor('SupportUser', {
            id: 'supportUser',
            header: 'Support Account',
            cell: ({ value }) => {
                const displayName =
                    value.firstName || value.lastName
                        ? `${value.firstName ?? ''} ${value.lastName ?? ''}`.trim()
                        : value.email;
                return (
                    <Tooltip title={value.email}>
                        <Button.Link
                            variant="link"
                            to="/admin-console/user-access/users/edit/$userId"
                            params={{ userId: value.id }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayName}
                        </Button.Link>
                    </Tooltip>
                );
            },
        }),
        columnHelper.accessor('requestedRoles', {
            id: 'roles',
            header: 'Requested Roles',
            cell: ({ value }) => (
                <div className="flex flex-wrap gap-1">
                    {(value as Role[]).slice(0, 2).map((role) => (
                        <Tag key={role} variant="outlined" color="blue">
                            {getRoleDisplayName(role)}
                        </Tag>
                    ))}
                    {(value as Role[]).length > 2 && (
                        <Tooltip title={(value as Role[]).slice(2).map(getRoleDisplayName).join(', ')}>
                            <Tag variant="outlined">+{(value as Role[]).length - 2}</Tag>
                        </Tooltip>
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ value }) => (
                <Tag variant="outlined" color={getStatusColor(value as SupportSessionStatus)}>
                    {SUPPORT_SESSION_STATUS_LABELS[value as SupportSessionStatus]}
                </Tag>
            ),
            width: 140,
        }),
        columnHelper.accessor('expiresAt', {
            id: 'expiresAt',
            header: 'Expires',
            sortable: 'expiresAt',
            cell: ({ value, row }) => {
                const isExpired = new Date(value) < new Date() && row.status === 'approved';
                const formatted = DateTime.fromJSDate(value).toFormat('dd/MM/yyyy HH:mm');
                return <span className={isExpired ? 'text-red-500' : ''}>{formatted}</span>;
            },
        }),
        columnHelper.accessor('RequestedByUser', {
            id: 'requestedBy',
            header: 'Requested By',
            cell: ({ value }) => {
                const displayName =
                    value.firstName || value.lastName
                        ? `${value.firstName ?? ''} ${value.lastName ?? ''}`.trim()
                        : value.email;
                return (
                    <Tooltip title={value.email}>
                        <Button.Link
                            variant="link"
                            to="/admin-console/user-access/users/edit/$userId"
                            params={{ userId: value.id }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayName}
                        </Button.Link>
                    </Tooltip>
                );
            },
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created',
            sortable: 'createdAt',
            cell: ({ value }) => formatDate(value),
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Space size="small">
                    <Button
                        type="tertiary"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSession(row);
                        }}
                    >
                        View
                    </Button>

                    {row.status === 'pending' && (
                        <Button
                            type="tertiary"
                            danger
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCancel(row);
                            }}
                            icon={<Trash2 />}
                        >
                            Cancel
                        </Button>
                    )}
                    {row.status === 'approved' && (
                        <Button
                            type="tertiary"
                            danger
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRevoke(row);
                            }}
                            icon={<Trash2 />}
                        >
                            Revoke
                        </Button>
                    )}
                </Space>
            ),
            width: 150,
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-support-sessions-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <TableToolbar
                    filters={<SupportSessionFilters onChange={setFilters} />}
                    actions={
                        canRequestSupportSession ? (
                            <Button type="primary" icon={<Plus />} onClick={() => setIsRequestDrawerOpen(true)}>
                                Request session
                            </Button>
                        ) : null
                    }
                    variant="enhanced"
                />

                <RequestSupportSessionDrawer open={isRequestDrawerOpen} onClose={() => setIsRequestDrawerOpen(false)} />

                <SupportSessionDetailsDrawer
                    session={selectedSession}
                    open={!!selectedSession}
                    onClose={() => setSelectedSession(null)}
                />

                <PrismaTable {...tableProps} />
            </TRPCErrorBoundary>
        </PageController>
    );
}
